import { getApiOrigin, getApiTimeoutMs } from "./config";

export interface ApiErrorShape {
  status?: string;
  message?: string;
  details?: unknown;
}

export interface ApiFetchOptions extends RequestInit {
  token?: string | null;
  timeoutMs?: number;
  query?: Record<string, string | number | boolean | null | undefined>;
}

export class ApiError extends Error {
  status: number;
  details?: unknown;

  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.details = details;
  }
}

function buildUrl(path: string, query?: ApiFetchOptions["query"]) {
  const origin = getApiOrigin();
  const url = new URL(path.startsWith("http") ? path : `${origin}${path.startsWith("/") ? path : `/${path}`}`);

  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value !== null && typeof value !== "undefined" && value !== "") {
        url.searchParams.set(key, String(value));
      }
    });
  }

  return url.toString();
}

function getStoredToken() {
  if (typeof window === "undefined") return null;

  return window.localStorage.getItem("gu_access_token");
}

export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const { token, timeoutMs = getApiTimeoutMs(), query, headers, body, ...fetchOptions } = options;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  const authToken = token ?? getStoredToken();

  try {
    const response = await fetch(buildUrl(path, query), {
      credentials: "include",
      ...fetchOptions,
      signal: controller.signal,
      headers: {
        Accept: "application/json",
        ...(body ? { "Content-Type": "application/json" } : {}),
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        ...headers,
      },
      body,
    });

    const contentType = response.headers.get("content-type") || "";
    const payload = contentType.includes("application/json")
      ? ((await response.json()) as T & ApiErrorShape)
      : ((await response.text()) as T & ApiErrorShape);

    if (!response.ok) {
      const message =
        typeof payload === "object" && payload && "message" in payload
          ? String((payload as ApiErrorShape).message || response.statusText)
          : response.statusText;

      throw new ApiError(message, response.status, typeof payload === "object" ? payload : undefined);
    }

    return payload as T;
  } catch (error) {
    if (error instanceof ApiError) throw error;

    if (error instanceof Error && error.name === "AbortError") {
      throw new ApiError("The Green Unison API request timed out.", 408);
    }

    throw error;
  } finally {
    clearTimeout(timeout);
  }
}

export function setAuthToken(token: string | null) {
  if (typeof window === "undefined") return;

  if (token) {
    window.localStorage.setItem("gu_access_token", token);
  } else {
    window.localStorage.removeItem("gu_access_token");
  }
}

export function getAuthToken() {
  return getStoredToken();
}
